import { FormEvent, useRef, useState } from 'react';
import type { ExpenseCostSplit, ExpenseType } from '../../api';
import { useFeedback } from '../../hooks/useFeedback';
import {
  EXPENSE_ADD_TYPE_OPTIONS,
  splitsForExpenseType,
} from '../../utils/expenseTypes';
import { manualShareError, manualSharePairs, sharesFromSaved, hoursFlightIdsPayload } from '../../utils/expenseSplit';
import ExpenseSplitFields from '../ExpenseSplitFields/ExpenseSplitFields';
import '../modal/ModalBase.css';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import './AddExpenseModal.css';

export type ExpenseFormPayload = {
  expense_date: string;
  expense_type: ExpenseType;
  amount: number;
  cost_split: ExpenseCostSplit;
  payer_ids?: number[];
  payer_shares?: number[];
  flight_ids?: number[];
  title?: string;
  comment?: string;
};

export type PersonalExpenseFormPayload = {
  expense_date: string;
  amount: number;
  description: string;
};

type BaseProps = {
  onClose: () => void;
  title?: string;
  confirmLabel?: string;
};

type AircraftProps = BaseProps & {
  variant?: 'aircraft';
  onConfirm: (data: ExpenseFormPayload) => void | Promise<void>;
  initial?: Partial<ExpenseFormPayload> | null;
};

type PersonalProps = BaseProps & {
  variant: 'personal';
  onConfirm: (data: PersonalExpenseFormPayload) => void | Promise<void>;
  initial?: Partial<PersonalExpenseFormPayload> | null;
};

type Props = AircraftProps | PersonalProps;

const todayIso = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const toDateInput = (value?: string) => {
  if (!value) return todayIso();
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return todayIso();
};

const AddExpenseModal = (props: Props) => {
  const {
    onClose,
    title = props.variant === 'personal'
      ? 'Добавить личный расход'
      : 'Добавить расход',
    confirmLabel = 'Добавить',
    initial = null,
  } = props;
  const isPersonal = props.variant === 'personal';
  const { showError, feedbackNode } = useFeedback();
  const [form, setForm] = useState({
    expense_date: toDateInput(initial?.expense_date),
    expense_type: (
      !isPersonal && initial && 'expense_type' in initial
        ? initial.expense_type || ''
        : ''
    ) as '' | ExpenseType,
    amount:
      initial?.amount != null && Number.isFinite(Number(initial.amount))
        ? String(initial.amount)
        : '0',
    description:
      isPersonal && initial && 'description' in initial
        ? initial.description || ''
        : '',
    title:
      !isPersonal && initial && 'title' in initial ? initial.title || '' : '',
    comment:
      !isPersonal && initial && 'comment' in initial
        ? initial.comment || ''
        : '',
    cost_split: (
      !isPersonal && initial && 'cost_split' in initial && initial.cost_split
        ? initial.cost_split
        : 'shares'
    ) as ExpenseCostSplit,
  });
  const [manualShares, setManualShares] = useState<Record<number, string>>(() => {
    if (isPersonal || !initial || !('payer_ids' in initial) || !initial.payer_ids) {
      return {};
    }
    return sharesFromSaved(initial.payer_ids, initial.payer_shares);
  });
  const [flightIds, setFlightIds] = useState<number[]>(() =>
    !isPersonal && initial && 'flight_ids' in initial && initial.flight_ids
      ? initial.flight_ids
      : []
  );
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const amountValue = Number(String(form.amount).trim().replace(',', '.'));
  const previewAmount =
    String(form.amount).trim() === ''
      ? 0
      : Number.isFinite(amountValue) && amountValue >= 0
        ? amountValue
        : null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!form.expense_date) {
      showError('Укажите дату');
      return;
    }
    const amountRaw = String(form.amount).trim().replace(',', '.');
    const amount = amountRaw === '' ? 0 : Number(amountRaw);
    if (!Number.isFinite(amount) || amount < 0) {
      showError('Укажите сумму в рублях');
      return;
    }
    if (isPersonal) {
      const description = form.description.trim();
      if (!description) {
        showError('Укажите описание');
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await props.onConfirm({
          expense_date: form.expense_date,
          amount,
          description,
        });
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
      return;
    }
    if (!form.expense_type) {
      showError('Выберите вид расхода');
      return;
    }
    const customTitle = form.title.trim();
    if (form.expense_type === 'other' && !customTitle) {
      showError('Укажите название');
      return;
    }
    if (form.cost_split === 'hours' && flightIds.length === 0) {
      showError('Выберите полёты для пропорционального расчёта');
      return;
    }
    if (form.cost_split === 'manual') {
      const error = manualShareError(manualShares);
      if (error) {
        showError(error);
        return;
      }
    }
    const pairs = manualSharePairs(manualShares);
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await props.onConfirm({
        expense_date: form.expense_date,
        expense_type: form.expense_type,
        amount,
        cost_split: form.cost_split,
        payer_ids: form.cost_split === 'manual' ? pairs.map((row) => row.user_id) : undefined,
        payer_shares:
          form.cost_split === 'manual' ? pairs.map((row) => row.share) : undefined,
        flight_ids: hoursFlightIdsPayload(
          form.cost_split,
          flightIds,
          !isPersonal && initial && 'flight_ids' in initial
            ? initial.flight_ids
            : undefined
        ),
        title: form.expense_type === 'other' ? customTitle : undefined,
        comment: form.comment.trim(),
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose}>
        {feedbackNode}
        <form
          className="modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>{title}</h3>
          <div className="modal-body">
            <label>Дата</label>
            <input
              type="date"
              value={form.expense_date}
              onChange={(e) =>
                setForm({ ...form, expense_date: e.target.value })
              }
              required
            />

            {isPersonal ? (
              <>
                <label>Сумма, ₽</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="0"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: e.target.value })
                  }
                />
                <label>Описание</label>
                <input
                  type="text"
                  placeholder="Описание"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  required
                  maxLength={500}
                />
              </>
            ) : (
              <>
                <label>Вид</label>
                <select
                  value={form.expense_type}
                  onChange={(e) => {
                    const next = e.target.value as ExpenseType;
                    const allowed = splitsForExpenseType(next);
                    setForm({
                      ...form,
                      expense_type: next,
                      cost_split: allowed.includes(form.cost_split)
                        ? form.cost_split
                        : allowed[0],
                    });
                  }}
                  required
                >
                  <option value="" disabled>
                    Выберите вид
                  </option>
                  {EXPENSE_ADD_TYPE_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>

                {form.expense_type === 'other' ? (
                  <>
                    <label>Название</label>
                    <input
                      type="text"
                      placeholder="Свой вариант"
                      value={form.title}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                      maxLength={100}
                      required
                    />
                  </>
                ) : null}

                <label>Сумма, ₽</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="0"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: e.target.value })
                  }
                />

                <ExpenseSplitFields
                  date={form.expense_date}
                  amount={previewAmount}
                  expenseType={form.expense_type || 'oil'}
                  costSplit={form.cost_split}
                  manualShares={manualShares}
                  flightIds={flightIds}
                  onCostSplitChange={(value) =>
                    setForm({ ...form, cost_split: value })
                  }
                  onManualSharesChange={setManualShares}
                  onFlightIdsChange={setFlightIds}
                />

                <label>Комментарий</label>
                <textarea
                  value={form.comment}
                  onChange={(e) =>
                    setForm({ ...form, comment: e.target.value })
                  }
                  placeholder="Любая информация по этому расходу..."
                  rows={5}
                  maxLength={2000}
                />
              </>
            )}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? '…' : confirmLabel}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AddExpenseModal;
